/**
 * VisibilityAwareTimer - Intervals that throttle when the tab is hidden
 *
 * Use for non-critical timers (proximity checks, nameplate updates, etc.)
 * to reduce CPU and memory when the tab is not focused.
 *
 * Critical timers (network, Colyseus, LiveKit connection) should NOT use this.
 */

const PAGE_HIDDEN_EVENT = 'page-hidden'
const PAGE_VISIBLE_EVENT = 'page-visible'

/**
 * Creates an interval that runs at `activeDelay` when the tab is visible
 * and at `hiddenDelay` when the tab is hidden.
 *
 * @param callback - Function to call on each tick
 * @param activeDelay - Interval in ms when tab is visible
 * @param hiddenDelay - Interval in ms when tab is hidden
 * @returns Cleanup function to clear the interval
 */
export function setVisibilityAwareInterval(
  callback: () => void,
  activeDelay: number,
  hiddenDelay: number
): () => void {
  let intervalId: ReturnType<typeof setInterval> | null = null
  let isVisible = !document.hidden

  const getDelay = () => (isVisible ? activeDelay : hiddenDelay)

  const schedule = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    const delay = getDelay()
    intervalId = setInterval(() => {
      callback()
    }, delay)
  }

  const handlePageHidden = () => {
    isVisible = false
    schedule()
  }

  const handlePageVisible = () => {
    isVisible = true
    // Immediately run the callback so stale proximity / audio state is
    // re-evaluated the moment the user returns to this tab, rather than
    // waiting up to `hiddenDelay` ms for the next scheduled tick.
    callback()
    schedule()
  }

  schedule()
  window.addEventListener(PAGE_HIDDEN_EVENT, handlePageHidden)
  window.addEventListener(PAGE_VISIBLE_EVENT, handlePageVisible)

  return () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    window.removeEventListener(PAGE_HIDDEN_EVENT, handlePageHidden)
    window.removeEventListener(PAGE_VISIBLE_EVENT, handlePageVisible)
  }
}

